"use client";

import { cn } from "@/lib/utils";
import { isSportCategory } from "@yn/utils";
import { forwardRef } from "react";
import { FootballMainGuessEventCard, hasFootballMainGuess } from "./football-main-guess-event-card";
import { NormalEventCard, type NormalEventCardProps } from "./normal-event-card";
import { SportEventCard } from "./sport-event-card";
import { SportSimpleEventCard } from "./sport-simple-event-card";

// EventCard 组件的 ref 类型定义（兼容旧代码）
export interface EventCardRef {
    collectDOMInfo: () => any;
}

// EventCard Props（继承 NormalEventCard 的 props）
export interface EventCardProps extends NormalEventCardProps {
    eventInfo: any;
    /** 当前分类是否为赛事分类 */
    ifSport?: boolean;
}

// ============ EventCard Factory Component ============
/**
 * EventCard 工厂组件
 * 根据 eventInfo.eventCategory 和 ifSport 判断渲染哪种卡片：
 * - eventCategory === 0: 普通事件卡片（NormalEventCard）
 * - eventCategory !== 0 && ifSport === true: 赛事卡片（SportEventCard）
 * - eventCategory !== 0 && ifSport !== true: 赛事简化卡片（SportSimpleEventCard）
 */
export default forwardRef<EventCardRef, EventCardProps>( function EventCard( props, ref ) {
    const {
        eventInfo,
        shadow = true,
        className,
        isLive = false,
        showLiveLabel = true,
        ifSport = false,
        inCard = false,
        imageSize = "default",
        ...restProps
    } = props;
    // 判断是否是赛事卡片
    const isSportEvent = isSportCategory( eventInfo.eventCategory );
    // 赛事卡片
    if ( isSportEvent ) {
        if ( hasFootballMainGuess( eventInfo ) ) {
            return (
                <FootballMainGuessEventCard
                    ref={ref}
                    eventInfo={eventInfo}
                    className={className}
                    orderType={restProps.orderType}
                    onCardClick={restProps.onCardClick}
                />
            );
        }

        // 赛事分类下使用完整赛事卡片
        if ( ifSport ) {
            return (
                <div className={cn( "overflow-hidden", className )}>
                    <SportEventCard
                        ref={ref}
                        eventInfo={eventInfo}
                        orderType={restProps.orderType}
                        onCardClick={restProps.onCardClick}
                    />
                </div>
            );
        }

        // 非赛事分类下使用简化赛事卡片
        return (
            <SportSimpleEventCard
                ref={ref}
                eventInfo={eventInfo}
                imageSize={imageSize}
                orderType={restProps.orderType}
                onCardClick={restProps.onCardClick}
            />
        );
    }

    // 普通事件卡片
    return (
        <NormalEventCard
            ref={ref}
            eventInfo={eventInfo}
            shadow={shadow}
            isLive={isLive}
            className={cn( className, inCard && "px-4" )}
            imageSize={imageSize}
            {...restProps}
        />
    );
} );
